import readCurrentFacility from '@Application/Facilities/CurrentFacility/readCurrentFacility';
import recordCurrentFacility from '@Application/Facilities/CurrentFacility/recordCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import CurrentFacilityContext, {
	type CurrentFacilityContextValue,
} from '@UI/Providers/CurrentFacilityProvider/CurrentFacilityContext';
import type AccountFacilityView from '@UI/Store/AccountFacilities/Views/AccountFacilityView';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

const sortByName = (list: readonly AccountFacilityView[]): readonly AccountFacilityView[] =>
	[...list].sort((left, right) => left.name.localeCompare(right.name));

type Props = {
	readonly children: React.ReactNode;
};

/**
 * Mounted below `CurrentOrganizationProvider`, inside `AppShell`. Reads the current organization's
 * facilities straight off the composed organization (via `useCurrentOrganization`) instead of fetching
 * them itself — the two fetches, the account resolution and the "no account" short-circuit all live one
 * level up.
 *
 * Two distinct resets are deliberately not conflated:
 * - **No current organization**: `hasConfirmedNoOrganizations` (from `useCurrentOrganization`) gates
 *   whether the persisted facility id is purged. It is only ever `true` once the server has actually
 *   confirmed zero organizations — `currentOrganization` is also `null` while there is no account to
 *   query yet, while either the organizations or the facilities fetch has failed
 *   (`areOrganizationsConfirmed` / `areFacilitiesConfirmed`), or before both have resolved, and none of
 *   those synthetic resolutions must ever be mistaken for a server response confirming zero facilities:
 *   doing so would erase a legitimately persisted identifier on a transient network error, with no way
 *   back for the user. The in-memory `currentFacilityId` still resets to `null` in every case — there is
 *   no organization to read facilities from — only the persisted value is spared.
 * - **First resolution** (this session's first non-null organization): the three-test validation below,
 *   gated on the facilities list itself rather than on `isResolved` — a synthetic resolution (no
 *   organization to interrogate, or a genuinely empty list) must never be mistaken for a server answer
 *   confirming zero facilities, which would purge a legitimately persisted identifier during the
 *   transient render before the organization is known. The purge itself additionally requires
 *   `areFacilitiesConfirmed`: `currentOrganization` can in practice only be non-null once both fetches
 *   already succeeded (see `CurrentOrganizationProvider`'s selection effect), but this branch does not
 *   rely on that invariant holding forever. An empty list always wins over what is persisted, and there
 *   is **no automatic reselection** when the persisted identifier is no longer assigned: the current
 *   facility becomes `null` and picking another one is left to the user.
 * - **A later organization change** (`currentOrganization` no longer matches the resolved facility):
 *   the two contexts cannot diverge, so this lands on the first facility of the new organization, or
 *   `null` if it has none — without consulting `localStorage`, whose persisted id necessarily belonged
 *   to the organization just left. This branch is skipped when the facility is already consistent with
 *   the new organization, which is what lets `FacilitySelector` pick a *specific* facility across an
 *   organization boundary (it sets both contexts together, in the same event) rather than always
 *   landing on the alphabetically-first one.
 *
 * `isResolved` on the exposed context is this provider's own settlement, not the organization
 * context's: it stays `false` for the one render where the organization fetches have already settled
 * but `CurrentOrganizationProvider`'s own selection effect has not yet picked a `currentOrganization`,
 * so every consumer's "no facility" branch never flashes ahead of the real answer.
 */
const CurrentFacilityProvider: React.FC<Props> = ({ children }) => {
	const navigate = useNavigate();
	const {
		currentOrganization,
		organizations,
		isResolved: isOrganizationResolved,
		hasConfirmedNoOrganizations,
		areOrganizationsConfirmed,
		areFacilitiesConfirmed,
	} = useCurrentOrganization();

	const facilities = React.useMemo<readonly AccountFacilityView[]>(() => {
		if (currentOrganization === null) {
			return [];
		}

		return sortByName(currentOrganization.facilities);
	}, [currentOrganization]);

	const [currentFacilityId, setCurrentFacilityId] = React.useState<number | null>(null);
	const hasResolvedInitialFacilityRef = React.useRef(false);
	// Tracks whether the resolution effect below has settled for the current organization context,
	// independently of `hasResolvedInitialFacilityRef` (which answers "is this the session's first
	// resolution?", not "is `isResolved` true right now?"). Never read inside the effect itself — it is
	// only ever written there — so it never belongs in the effect's own dependency array, which is what
	// keeps this from looping.
	const [isFacilityResolved, setIsFacilityResolved] = React.useState(false);

	React.useEffect(() => {
		if (!isOrganizationResolved) {
			setIsFacilityResolved(false);
			return;
		}

		if (currentOrganization === null) {
			// `isOrganizationResolved` can be `true` for one render before `CurrentOrganizationProvider`'s own
			// selection effect has picked an organization: both fetches already succeeded — the only case its
			// selection effect ever runs from — but `currentOrganizationId` is still the initial `null` until
			// that effect fires. Guarded on both confirmations (not just `organizations.length`): if either
			// fetch instead settled on an error, that selection effect never runs at all and `currentOrganization`
			// stays `null` forever, so waiting for it here would spin indefinitely instead of settling like the
			// rest of this provider always has on a startup failure.
			const isOrganizationSelectionPending =
				areOrganizationsConfirmed && areFacilitiesConfirmed && organizations.length > 0;

			if (isOrganizationSelectionPending) {
				return;
			}

			// Only a server-confirmed empty organization list clears the persisted facility: `currentOrganization`
			// is also `null` while there is no account to query yet, or while either fetch has failed, and
			// neither must purge what the user last chose — an error is not a confirmed zero.
			if (hasConfirmedNoOrganizations) {
				recordCurrentFacility(null);
			}

			setCurrentFacilityId(null);
			setIsFacilityResolved(true);
			return;
		}

		if (currentFacilityId !== null && facilities.some((facility) => facility.id === currentFacilityId)) {
			setIsFacilityResolved(true);
			return;
		}

		if (!hasResolvedInitialFacilityRef.current) {
			hasResolvedInitialFacilityRef.current = true;

			if (facilities.length === 0) {
				// Purging what is persisted still requires the facilities fetch to have actually
				// succeeded — see the docblock above: `currentOrganization` should only ever be non-null
				// once it has, but this guard does not lean on that holding forever.
				if (areFacilitiesConfirmed) {
					recordCurrentFacility(null);
				}

				setCurrentFacilityId(null);
				setIsFacilityResolved(true);
				return;
			}

			const persistedFacilityId = readCurrentFacility();

			if (persistedFacilityId === null) {
				const defaultFacility = facilities[0];

				if (defaultFacility === undefined) {
					setIsFacilityResolved(true);
					return;
				}

				recordCurrentFacility(defaultFacility.id);
				setCurrentFacilityId(defaultFacility.id);
				setIsFacilityResolved(true);
				return;
			}

			const isPersistedFacilityStillAssigned = facilities.some(
				(facility) => facility.id === persistedFacilityId,
			);

			setCurrentFacilityId(isPersistedFacilityStillAssigned ? persistedFacilityId : null);
			setIsFacilityResolved(true);
			return;
		}

		const firstFacility = facilities[0];
		recordCurrentFacility(firstFacility?.id ?? null);
		setCurrentFacilityId(firstFacility?.id ?? null);
		setIsFacilityResolved(true);
	}, [
		currentOrganization,
		organizations,
		isOrganizationResolved,
		hasConfirmedNoOrganizations,
		areOrganizationsConfirmed,
		areFacilitiesConfirmed,
		facilities,
		currentFacilityId,
	]);

	const currentFacility = React.useMemo<AccountFacilityView | null>(() => {
		if (currentFacilityId === null) {
			return null;
		}

		return facilities.find((facility) => facility.id === currentFacilityId) ?? null;
	}, [facilities, currentFacilityId]);

	const selectFacility = React.useCallback(
		(facilityId: number, options?: { readonly redirectTo?: string }) => {
			recordCurrentFacility(facilityId);
			setCurrentFacilityId(facilityId);
			// Resets the navigation context: never stay on a screen scoped to the facility just left.
			navigate(options?.redirectTo ?? '/');
		},
		[navigate],
	);

	const value = React.useMemo<CurrentFacilityContextValue>(
		() => ({ currentFacility, facilities, isResolved: isFacilityResolved, selectFacility }),
		[currentFacility, facilities, isFacilityResolved, selectFacility],
	);

	return <CurrentFacilityContext.Provider value={value}>{children}</CurrentFacilityContext.Provider>;
};

export default CurrentFacilityProvider;
